import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Download, Building2, Globe, Users, Award, ShieldOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FILTERS = {
  classification: '',
  maicpp_tier:    '',
  partner_type:   '',
  domain_source:  '',
  search:         '',
};

// Display label overrides for DB values
const CLASSIFICATION_LABELS = {
  ESI_MPL_GSI: 'MAICPP ESI',
  CSP:         'CSP Reseller',
};

const CLASSIFICATION_COLORS = {
  ESI_MPL_GSI:      'badge-green',
  CSP:              'badge-blue',
  CSP_DISTI:        'badge-blue',
  CSP_NON_DISTI:    'badge-teal',
  ManagedReseller:  'badge-purple',
  MANAGED_RESELLER: 'badge-purple',
  MaicppUnmanaged:  'badge-orange',
  MAICPP_UNMANAGED: 'badge-orange',
  Recruit:          'badge-yellow',
  RECRUIT:          'badge-yellow',
  Unclassified:     'badge-gray',
};

const SOURCE_COLORS = {
  ESI:      'badge-green',
  TOP50_CSP:'badge-blue',
};

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="pagination" data-testid="partner-pagination">
      <span className="pagination-info">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

function ClassificationBadge({ value, showEmpty = false }) {
  if (!value || value === 'Unclassified') {
    if (showEmpty) {
      return <span className="badge badge-gray" style={{ fontSize: 11 }}>Unclassified</span>;
    }
    return <span className="badge badge-gray" style={{ fontSize: 11, color: '#999' }}>—</span>;
  }
  const cls   = CLASSIFICATION_COLORS[value] || 'badge-gray';
  const label = CLASSIFICATION_LABELS[value]  || value;
  return (
    <span className={`badge ${cls}`} style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function SourceBadge({ value }) {
  if (!value) return <span style={{ color: '#ccc' }}>—</span>;
  const cls = SOURCE_COLORS[value] || 'badge-gray';
  return (
    <span className={`badge ${cls}`} style={{ fontSize: 10 }}>
      {value === 'TOP50_CSP' ? 'Top50 CSP' : value}
    </span>
  );
}

function Top50Badge({ isTop50 }) {
  if (!isTop50) return null;
  return <span className="badge badge-yellow" style={{ fontSize: 10, marginLeft: 4 }}>Top 50</span>;
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview]           = useState(null);
  const [filterOpts, setFilterOpts]       = useState({});
  const [filters, setFilters]             = useState(EMPTY_FILTERS);
  const [applied, setApplied]             = useState(EMPTY_FILTERS);
  const [data, setData]                   = useState([]);
  const [total, setTotal]                 = useState(0);
  const [totalPages, setTotalPages]       = useState(1);
  const [page, setPage]                   = useState(1);
  const [loading, setLoading]             = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [exporting, setExporting]         = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [loadedAt, setLoadedAt]           = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/partners/overview`, { withCredentials: true })
      .then(r => { setOverview(r.data); setOverviewLoading(false); setLoadedAt(new Date()); })
      .catch(() => setOverviewLoading(false));
    axios.get(`${API}/api/partners/filters`, { withCredentials: true })
      .then(r => setFilterOpts(r.data))
      .catch(console.error);
    fetchData(1, EMPTY_FILTERS);
  }, []); // eslint-disable-line

  const buildParams = (f, p) => {
    const q = { page: p, page_size: 25 };
    if (f.classification) q.classification = f.classification;
    if (f.maicpp_tier)    q.maicpp_tier    = f.maicpp_tier;
    if (f.partner_type)   q.partner_type   = f.partner_type;
    if (f.domain_source)  q.domain_source  = f.domain_source;
    if (f.search)         q.search         = f.search;
    return q;
  };

  const fetchData = useCallback(async (p, f) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/partners/list`,
        { params: buildParams(f, p), withCredentials: true });
      setData(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.total_pages);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  const handleApply = () => { setApplied(filters); fetchData(1, filters); };
  const handleReset = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); fetchData(1, EMPTY_FILTERS); };
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/api/partners/export`,
        { params: buildParams(applied, 1), withCredentials: true });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'partner_domains.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  const s1 = overview?.section1 || {};
  const s2 = overview?.section2 || {};

  return (
    <div data-testid="partner-dashboard-page">
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt; Partner Dashboard
      </div>
      <div className="page-header">
        <h1>Partner Dashboard</h1>
        <p style={{ color: '#666', fontSize: 14, margin: '4px 0 0' }}>
          Onboarded partner organisations and MAICPP domain pool overview
        </p>
      </div>

      {overviewLoading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
        </div>
      ) : (
        <>
          {/* ─────────────────────────────────────────────────────────────── */}
          {/* SECTION 1 — Onboarded Partners                                  */}
          {/* ─────────────────────────────────────────────────────────────── */}
          <div style={{ marginBottom: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#194449', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Onboarded Partners
            </h2>
          </div>

          {/* 4 KPI cards */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 16 }}>
            <div className="stat-card" data-testid="pd-stat-partners">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <Building2 size={20} color="#6CB4B0" />
              </div>
              <div className="stat-value green">{(s1.total_partners || 0).toLocaleString()}</div>
              <div className="stat-label">Total Partner Companies</div>
            </div>
            <div className="stat-card" data-testid="pd-stat-domains">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <Globe size={20} color="#8B77C8" />
              </div>
              <div className="stat-value green">{(s1.total_domains || 0).toLocaleString()}</div>
              <div className="stat-label">Total Registered Domains</div>
            </div>
            <div className="stat-card" data-testid="pd-stat-learners">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <Users size={20} color="#C87598" />
              </div>
              <div className="stat-value green">{(s1.total_learners || 0).toLocaleString()}</div>
              <div className="stat-label">Total Learners</div>
            </div>
            <div className="stat-card" data-testid="pd-stat-top50">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <Award size={20} color="#E8A840" />
              </div>
              <div className="stat-value green">{(s1.top50_csp_count || 0).toLocaleString()}</div>
              <div className="stat-label">Top 50 CSP Partners</div>
            </div>
          </div>

          {/* Classification tiles — hierarchical layout */}
          {/* Row 1: Two primary classification tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 10 }}>
            <div className="stat-card" data-testid="pd-class-maicpp-esi"
              style={{ borderTop: '3px solid #6CB4B0' }}>
              <div style={{ fontSize: 11, color: '#718096', marginBottom: 6, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                MAICPP ESI
              </div>
              <div className="stat-value green">{(s1.classification?.maicpp_esi || 0).toLocaleString()}</div>
              <div className="stat-label">partners</div>
            </div>
            <div className="stat-card" data-testid="pd-class-csp-reseller"
              style={{ borderTop: '3px solid #8B77C8' }}>
              <div style={{ fontSize: 11, color: '#718096', marginBottom: 6, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                CSP Reseller
              </div>
              <div className="stat-value green">{(s1.classification?.csp_reseller || 0).toLocaleString()}</div>
              <div className="stat-label">partners</div>
            </div>
          </div>

          {/* Row 2: Non-MAICPP callout bar — secondary subset note */}
          <div data-testid="pd-class-non-maicpp" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#F4F3F5', borderLeft: '4px solid #9CA3AF',
            borderRadius: 6, padding: '12px 16px', marginBottom: 28,
          }}>
            <div>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                NON-MAICPP
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#D4EC8E',
                  textShadow: '0 1px 2px rgba(0,0,0,0.18)' }}>
                  {(s1.classification?.non_maicpp || 0).toLocaleString()}
                </span>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>partners</span>
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic', marginTop: 2 }}>
                subset of {(s1.total_partners || 0).toLocaleString()} onboarded partners
              </div>
            </div>

            {/* Info icon with inline CSS tooltip */}
            <div style={{ position: 'relative', flexShrink: 0 }}
              onMouseEnter={() => setTooltipVisible(true)}
              onMouseLeave={() => setTooltipVisible(false)}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                border: '1.5px solid #9CA3AF',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'default', color: '#9CA3AF', fontSize: 12, fontWeight: 700,
                userSelect: 'none',
              }}>i</div>
              {tooltipVisible && (
                <div style={{
                  position: 'absolute', bottom: 'calc(100% + 8px)', right: 0,
                  background: '#091F2C', color: '#fff', borderRadius: 4,
                  padding: '8px 12px', maxWidth: 280, fontSize: '0.75rem',
                  lineHeight: 1.5, zIndex: 50, whiteSpace: 'normal',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)', pointerEvents: 'none',
                }}>
                  These {(s1.classification?.non_maicpp || 0).toLocaleString()} onboarded partners
                  have no domains in the Microsoft MAICPP domain pool. They are formally onboarded
                  on LevelUp but fall outside the MAICPP programme scope.
                </div>
              )}
            </div>
          </div>

          {/* ─────────────────────────────────────────────────────────────── */}
          {/* SECTION 2 — MAICPP Domain Pool                                  */}
          {/* ─────────────────────────────────────────────────────────────── */}
          <div style={{
            background: '#f0f9f9',
            border: '1px solid #b2dfdb',
            borderLeft: '4px solid #26A69A',
            borderRadius: 10,
            padding: '20px 24px',
            marginBottom: 28,
          }} data-testid="pd-section-maicpp">
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: '#194449', margin: 0,
                textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                MAICPP Domain Pool
              </h2>
              {loadedAt && (
                <span style={{ fontSize: 11, color: '#78909C', fontStyle: 'italic' }}>
                  Last loaded {loadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 14 }}>
              <div style={{ background: '#fff', borderRadius: 8, padding: '14px 18px',
                border: '1px solid #b2dfdb' }} data-testid="pd-maicpp-total">
                <div style={{ fontSize: 22, fontWeight: 700, color: '#194449' }}>
                  {(s2.total_maicpp_domains || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 3 }}>Total MAICPP Domains</div>
              </div>
              <div style={{ background: '#fff', borderRadius: 8, padding: '14px 18px',
                border: '1px solid #b2dfdb' }} data-testid="pd-maicpp-onboarded">
                <div style={{ fontSize: 22, fontWeight: 700, color: '#194449' }}>
                  {(s2.also_in_onboarded || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 3 }}>Also in Onboarded Partners</div>
              </div>
              <div style={{ background: '#fff', borderRadius: 8, padding: '14px 18px',
                border: '1px solid #b2dfdb' }} data-testid="pd-maicpp-only">
                <div style={{ fontSize: 22, fontWeight: 700, color: '#194449' }}>
                  {(s2.maicpp_only || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#718096', marginTop: 3 }}>MAICPP-Only Domains</div>
              </div>
            </div>
            <p style={{ fontSize: 12, color: '#546E7A', margin: 0, fontStyle: 'italic' }}>
              MAICPP domain pool represents all Microsoft AI Cloud Partner Program eligible domains.
              Not all are formally onboarded on LevelUp.
            </p>
          </div>

          {/* ─────────────────────────────────────────────────────────────── */}
          {/* Registration Governance — Blocked Domains KPI                   */}
          {/* ─────────────────────────────────────────────────────────────── */}
          <div
            data-testid="pd-blocked-domains-tile"
            onClick={() => navigate('/partners/table/blocked-domains')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#fff8f5',
              border: '1px solid #fcd9c8',
              borderLeft: '4px solid #e56b2e',
              borderRadius: 10,
              padding: '14px 20px',
              marginBottom: 28,
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fff1ea'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff8f5'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <ShieldOff size={22} color="#e56b2e" style={{ flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 11, color: '#c2572a', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                  Registration Governance
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: '1.6rem', fontWeight: 700, color: '#e56b2e' }}>
                    {(s1.total_blocked_domains || 0).toLocaleString()}
                  </span>
                  <span style={{ fontSize: 13, color: '#9a5031' }}>domains blocked from registration</span>
                </div>
              </div>
            </div>
            <span style={{
              fontSize: 12, fontWeight: 600,
              color: '#e56b2e',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              View blocked domains →
            </span>
          </div>
        </>
      )}

      {/* ── Filters ── */}
      <div className="filter-bar">
        <div className="filter-group">
          <label className="filter-label">Classification</label>
          <select className="filter-select" value={filters.classification}
            onChange={e => setF('classification', e.target.value)}
            data-testid="pd-filter-classification">
            <option value="">All Classifications</option>
            {(filterOpts.classifications || []).map(c => (
              <option key={c} value={c}>{CLASSIFICATION_LABELS[c] || c}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">MAICPP Tier</label>
          <select className="filter-select" value={filters.maicpp_tier}
            onChange={e => setF('maicpp_tier', e.target.value)}
            data-testid="pd-filter-tier">
            <option value="">All Tiers</option>
            {(filterOpts.maicpp_tiers || []).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Partner Type</label>
          <select className="filter-select" value={filters.partner_type}
            onChange={e => setF('partner_type', e.target.value)}
            data-testid="pd-filter-type">
            <option value="">All Types</option>
            {(filterOpts.partner_types || []).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Domain Source</label>
          <select className="filter-select" value={filters.domain_source}
            onChange={e => setF('domain_source', e.target.value)}
            data-testid="pd-filter-source">
            <option value="">All Sources</option>
            <option value="ESI">ESI / MPL / GSI</option>
            <option value="TOP50_CSP">Top 50 CSP</option>
          </select>
        </div>
        <div className="filter-group" style={{ flex: 2 }}>
          <label className="filter-label">Search</label>
          <input type="text" className="filter-input"
            placeholder="domain, partner name, or POID…"
            value={filters.search} onChange={e => setF('search', e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleApply()}
            data-testid="pd-filter-search" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button className="btn btn-primary"   onClick={handleApply} data-testid="pd-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={handleReset} data-testid="pd-reset-btn">Reset</button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title">Partner Domains</span>
            <span className="table-count" style={{ marginLeft: 8 }}>
              ({total.toLocaleString()} domains)
            </span>
          </div>
          <button onClick={handleExport} disabled={exporting}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#217346',
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
            data-testid="pd-export-btn" title="Export to CSV">
            <Download size={16} /> {exporting ? 'Exporting…' : 'CSV'}
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : data.length === 0 ? (
          <div className="empty-state">
            <h3>No partner domains found</h3>
            <p>Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table data-testid="partner-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>POID</th>
                    <th>Partner Name</th>
                    <th>Classification</th>
                    <th>Source</th>
                    <th>Partner Type</th>
                    <th className="td-right">Allowed Seats</th>
                    <th className="td-right">Tech Limit</th>
                    <th className="td-right">Sales Limit</th>
                    <th>Managed By</th>
                    <th className="td-right">Learners</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={`${row.poid}-${row.domain}-${i}`} data-testid={`pd-row-${i}`}>
                      <td style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 13 }}>
                        {row.domain || '—'}
                      </td>
                      <td style={{ color: '#718096', fontSize: 12 }}>{row.poid || '—'}</td>
                      <td style={{ maxWidth: 220 }} title={row.partner_name}>
                        {row.partner_name || '—'}
                      </td>
                      <td>
                        <ClassificationBadge value={row.classification} />
                        <Top50Badge isTop50={row.is_top50_csp} />
                      </td>
                      <td><SourceBadge value={row.domain_source} /></td>
                      <td>
                        {row.partner_type
                          ? <span className="badge badge-blue" style={{ fontSize: 11 }}>{row.partner_type}</span>
                          : '—'}
                      </td>
                      <td className="td-right">{row.allowed_seats || '—'}</td>
                      <td className="td-right">{row.tech_limit    || '—'}</td>
                      <td className="td-right">{row.sales_limit   || '—'}</td>
                      <td style={{ fontSize: 12, color: '#718096', maxWidth: 130 }} title={row.managed_by}>
                        {row.managed_by || '—'}
                      </td>
                      <td className="td-right" style={{ fontWeight: 600 }}>
                        {(row.learner_count || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={25}
              onPage={p => fetchData(p, applied)} />
          </>
        )}
      </div>
    </div>
  );
}
